import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import {
  AlterarTenantDtoType,
  regimesTributarios,
  tenantBreadcrumbItems,
} from "data/tenant";
import { transformErrors } from "helpers/utils";
import { useEffect } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Link, Navigate, useParams } from "react-router-dom";

const EditarCliente = () => {
  const { id } = useParams();

  const { isLoading, isError, data } = useQuery<{
    data: AlterarTenantDtoType;
  }>({
    queryKey: ["tenant", id],
    queryFn: () => axios.get(`/api/admin/empresa/${id}`),
    enabled: !!id,
  });

  const mutation = useMutation<
    any,
    any,
    { id: string; data: AlterarTenantDtoType }
  >({
    mutationFn: ({ id, data }) => axios.put(`/api/admin/empresa/${id}`, data),
    onError: (error) => {
      const formattedErrors = transformErrors(error.response.data.errors);
      for (const [field, error] of Object.entries(formattedErrors)) {
        form.setError(field as any, error as any);
      }
    },
  });

  const empresa = data?.data;

  const form = useForm<AlterarTenantDtoType>();

  useEffect(() => {
    if (empresa) {
      console.log(empresa);
      form.reset(empresa);
    }
  }, [empresa]);

  if (isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );

  if (isError) {
    return <div>Ocorreu um erro ao carregar a empresa</div>;
  }

  if (!empresa) {
    return <div>Empresa não encontrada</div>;
  }

  const onSubmit: SubmitHandler<AlterarTenantDtoType> = (data) => {
    mutation.mutate({ data, id: (id as string) });
  };

  if (mutation.isSuccess) {
    return <Navigate to={`/revenda/clientes`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={tenantBreadcrumbItems(empresa?.codigo)} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar um cliente</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações do cliente
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                to={`/revenda/clientes/${id}/assinatura`}
                className="btn btn-secondary"
                as={Link}
                variant="phoenix-secondary"
              >
                Assinatura
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() => form.reset()}
              >
                Descartar
              </Button>

              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <h4 className="mb-3">Identificação do cliente</h4>
              <Row className="g-2 mb-3">
                <Form.Group as={Col} xs={12} xl={2} controlId="Código">
                  <Form.Label htmlFor="cnpj">Código</Form.Label>
                  <Form.Control disabled value={empresa.codigo} />
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={2} controlId="cnpj">
                  <Form.Label htmlFor="cnpj">CNPJ</Form.Label>
                  <Form.Control
                    placeholder="CNPJ"
                    {...form.register("cnpj", {
                      required: "Campo obrigatório",
                    })}
                    required
                    isInvalid={!!form.formState.errors.cnpj}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.cnpj?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={4} controlId="razaoSocial">
                  <Form.Label htmlFor="razaoSocial">Razão Social</Form.Label>
                  <Form.Control
                    placeholder="Razão social"
                    {...form.register("razaoSocial")}
                    required
                    isInvalid={!!form.formState.errors.razaoSocial}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.razaoSocial?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={4} controlId="nomeFantasia">
                  <Form.Label htmlFor="nomeFantasia">Nome Fantasia</Form.Label>
                  <Form.Control
                    placeholder="Nome fantasia"
                    {...form.register("nomeFantasia")}
                    required
                    isInvalid={!!form.formState.errors.nomeFantasia}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.nomeFantasia?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={12}
                  xl={2}
                  controlId="inscricaoEstadual"
                >
                  <Form.Label htmlFor="inscricaoEstadual">
                    Inscrição Estadual
                  </Form.Label>
                  <Form.Control
                    placeholder="Inscrição estadual"
                    {...form.register("inscricaoEstadual")}
                    required
                    isInvalid={!!form.formState.errors.inscricaoEstadual}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.inscricaoEstadual?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={12}
                  xl={2}
                  controlId="regimeTributario"
                >
                  <Form.Label htmlFor="regimeTributario">
                    Regime tributário
                  </Form.Label>
                  <Form.Select {...form.register("regimeTributario")}>
                    {regimesTributarios.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.regimeTributario?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <h4 className="mb-3">Dados de contato</h4>
              <Row className="g-2 mb-3">
                <Form.Group as={Col} xs={12} xl={2} controlId="telefone">
                  <Form.Label htmlFor="telefone">Telefone</Form.Label>
                  <Form.Control
                    placeholder="Telefone"
                    {...form.register("telefone")}
                    required
                    isInvalid={!!form.formState.errors.telefone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.telefone?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={4} controlId="email">
                  <Form.Label htmlFor="email">E-mail</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="E-mail"
                    {...form.register("email")}
                    required
                    isInvalid={!!form.formState.errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.email?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} xl={6} controlId="responsavel">
                  <Form.Label htmlFor="responsavel">Responsável</Form.Label>
                  <Form.Control
                    placeholder="Responsável"
                    {...form.register("responsavel")}
                    required
                    isInvalid={!!form.formState.errors.responsavel}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.responsavel?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <h4 className="mb-3">Endereço</h4>
              <Row className="g-2 mb-3">
                <Form.Group as={Col} xs={12} xl={2} controlId="endereco.cep">
                  <Form.Label htmlFor="endereco.cep">CEP</Form.Label>
                  <Form.Control
                    placeholder="CEP"
                    {...form.register("endereco.cep")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.cep}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.cep?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={8}
                  xl={8}
                  controlId="endereco.logradouro"
                >
                  <Form.Label htmlFor="endereco.logradouro">
                    Logradouro
                  </Form.Label>
                  <Form.Control
                    placeholder="Logradouro"
                    {...form.register("endereco.logradouro")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.logradouro}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.logradouro?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={4} xl={2} controlId="endereco.numero">
                  <Form.Label htmlFor="endereco.numero">Número</Form.Label>
                  <Form.Control
                    placeholder="Número"
                    {...form.register("endereco.numero")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.numero}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.numero?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  xs={12}
                  xl={4}
                  controlId="endereco.complemento"
                >
                  <Form.Label htmlFor="endereco.complemento">
                    Complemento
                  </Form.Label>
                  <Form.Control
                    placeholder="Complemento"
                    {...form.register("endereco.complemento")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.complemento}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.complemento?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={6} xl={4} controlId="endereco.bairro">
                  <Form.Label htmlFor="endereco.bairro">Bairro</Form.Label>
                  <Form.Control
                    placeholder="Bairro"
                    {...form.register("endereco.bairro")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.bairro}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.bairro?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group
                  as={Col}
                  xs={6}
                  xl={4}
                  controlId="endereco.municipio"
                >
                  <Form.Label htmlFor="endereco.municipio">
                    Município
                  </Form.Label>
                  <Form.Control
                    placeholder="Município"
                    {...form.register("endereco.municipio")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.municipio}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.municipio?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={6} xl={2} controlId="endereco.uf">
                  <Form.Label htmlFor="endereco.uf">UF</Form.Label>
                  <Form.Control
                    placeholder="UF"
                    {...form.register("endereco.uf")}
                    required
                    isInvalid={!!form.formState.errors.endereco?.uf}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.endereco?.uf?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarCliente;
